import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtCurrencyCompact, fmtDate } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { ChartSkeleton } from '../components/Skeleton.tsx';

type Granularity = 'day' | 'week' | 'month' | 'year';
const GRAN_LABELS: Record<Granularity, string> = {
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
  year: 'Jahr',
};

export function TimeseriesChart() {
  const filters = useFilterStore((s) => s.filters);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const { data, isLoading } = useQuery({
    queryKey: ['timeseries', filters, granularity],
    queryFn: () => api.timeseries({ filters, granularity, compareVorjahr: true }),
  });

  if (isLoading) return <ChartSkeleton height={360} />;

  const labels = data?.map((p) => p.zeitpunkt) ?? [];
  const umsatz = data?.map((p) => p.umsatz) ?? [];
  const vorjahr = data?.map((p) => p.umsatz_vorjahr ?? null) ?? [];

  const option = {
    grid: { top: 20, right: 30, bottom: 40, left: 70, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: { color: COLORS.primary, type: 'dashed', opacity: 0.5 },
        label: {
          backgroundColor: COLORS.primary,
          formatter: (p: { value: string }) =>
            granularity === 'day' ? fmtDate(String(p.value)) : String(p.value),
        },
      },
      formatter: (params: Array<{ axisValue: string; seriesName: string; value: number; marker: string }>) => {
        const ts = params[0]?.axisValue ?? '';
        const head =
          granularity === 'day'
            ? fmtDate(ts)
            : granularity === 'month'
              ? ts.replace('-', '/')
              : ts;
        const rows = params
          .map(
            (p) =>
              `<div style="display:flex;justify-content:space-between;gap:16px;font-size:12px;line-height:1.6">
                 <span>${p.marker} ${p.seriesName}</span>
                 <span style="font-weight:600;font-variant-numeric:tabular-nums">${p.value != null ? fmtCurrency(p.value) : '—'}</span>
               </div>`,
          )
          .join('');
        return `<div><div style="font-weight:600;margin-bottom:4px;font-size:12px">${head}</div>${rows}</div>`;
      },
    },
    legend: {
      data: [
        { name: 'Umsatz', icon: 'circle' },
        { name: 'Vorjahr', icon: 'circle' },
      ],
      textStyle: { fontSize: 11, color: COLORS.text },
      top: 0,
      right: 0,
      itemWidth: 8,
      itemHeight: 8,
    },
    xAxis: {
      type: 'category',
      data: labels,
      ...axisStyle(),
      boundaryGap: false,
      axisLabel: {
        ...axisStyle().axisLabel,
        formatter: (v: string) => {
          if (granularity === 'day' && v.includes('-')) {
            const [, m, d] = v.split('-');
            return `${d}.${m}`;
          }
          return v;
        },
      },
    },
    yAxis: {
      type: 'value',
      ...axisStyle(),
      axisLabel: {
        ...axisStyle().axisLabel,
        formatter: (v: number) => fmtCurrencyCompact(v),
      },
    },
    series: [
      {
        name: 'Umsatz',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        sampling: 'lttb',
        emphasis: { focus: 'series', scale: true },
        data: umsatz,
        lineStyle: { width: 2.5, color: COLORS.primary },
        itemStyle: { color: COLORS.primary, borderColor: '#fff', borderWidth: 2 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(31,151,245,0.25)' },
              { offset: 1, color: 'rgba(31,151,245,0)' },
            ],
          },
        },
      },
      {
        name: 'Vorjahr',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: vorjahr,
        lineStyle: { width: 1.5, color: COLORS.vorjahr, type: 'dashed' },
        itemStyle: { color: COLORS.vorjahr },
      },
    ],
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
          {(['day', 'week', 'month', 'year'] as Granularity[]).map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                granularity === g
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              {GRAN_LABELS[g]}
            </button>
          ))}
        </div>
      </div>
      <ReactECharts
        option={option}
        style={{ height: 360 }}
        opts={{ renderer: 'canvas' }}
        notMerge={true}
      />
    </div>
  );
}
